import { createSelector } from "@reduxjs/toolkit";

import type {
  DesignerNodeData,
  GetNodeSlotId,
  GetNodeTypeIdById,
  SlotId,
} from "@skilitics/nautilus-core-designer-data";
import { getNodeSlotIdSelector, getNodeTypeIdSelector } from "@skilitics/nautilus-core-designer-data";

import type { GetManifestByTypeId } from "../../types/index.ts";
import { getNodeRoleCreate } from "../getNodeRole.ts";
import { getManifestSelector } from "./appConfigSelectors.ts";

export const getNodeRoleSelector = createSelector(
  [getManifestSelector, getNodeTypeIdSelector, getNodeSlotIdSelector],
  (getManifest, getNodeTypeId, getSlotId) => getNodeRoleCreate({ getManifest, getNodeTypeId, getSlotId }),
);

export const validateNodeRoleSelector = createSelector(
  [getManifestSelector, getNodeSlotIdSelector, getNodeTypeIdSelector],
  (getManifest, getSlotIdById, getTypeIdById) => validateNodeRoleCreate({ getManifest, getSlotIdById, getTypeIdById }),
);

interface CreateOptions {
  getManifest: GetManifestByTypeId;
  getSlotIdById: GetNodeSlotId;
  getTypeIdById: GetNodeTypeIdById;
}

type ChangingNodeRoleArg = Pick<DesignerNodeData, "id" | "role">;

type AddOrReplacingNodeArg = Pick<DesignerNodeData, "typeId" | "role"> &
  ({ slotId: SlotId | undefined } | { replacingId: number });

type Arg = ChangingNodeRoleArg | AddOrReplacingNodeArg;

export const validateNodeRoleCreate =
  ({ getManifest, getSlotIdById, getTypeIdById }: CreateOptions) =>
  (value: Arg) => {
    const validate = (typeId: string, slotId: SlotId | undefined) => {
      const manifest = getManifest(typeId);
      const manifestRoles = manifest.terminus.roles;
      if (!(manifestRoles && manifestRoles.length > 0) && value.role) {
        throw new Error(`No roles defined for node type ${typeId}.`);
      }
      if (manifestRoles && !manifestRoles.includes(value.role)) {
        throw new Error(`Role ${value.role} is not valid for node type ${typeId}.`);
      }
      if (slotId) {
        const parentTypeId = getTypeIdById(slotId.parentId);
        const parentManifest = getManifest(parentTypeId);
        const slotDefinition = parentManifest.terminus?.children?.[slotId.name];
        if (slotDefinition && "roles" in slotDefinition) {
          if (!slotDefinition.roles.includes(value.role)) {
            throw new Error(`Role ${value.role} is not valid for slot ${slotId.name} of ${parentTypeId}.`);
          }
        }
      }
    };

    if ("slotId" in value || "replacingId" in value) {
      const slotId = "slotId" in value ? value.slotId : getSlotIdById(value.replacingId);
      return validate(value.typeId, slotId);
    } else {
      const { id } = value;
      const typeId = getTypeIdById(id);
      const slotId = getSlotIdById(value.id);
      validate(typeId, slotId);
    }
  };

export type ValidateNodeRole = ReturnType<typeof validateNodeRoleCreate>;
